import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Gavel, Clock, DollarSign, Send, Loader2, Zap } from "lucide-react";

// Countdown parts derived from an ISO end-time. Recomputed each second.
function useCountdown(endIso) {
  const endMs = useMemo(() => (endIso ? new Date(endIso).getTime() : 0), [endIso]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = Math.max(0, endMs - now);
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return { d, h, m, s, ended: ms === 0 };
}

function Cell({ label, value }) {
  return (
    <div className="bg-[#0F1D1B] border border-[#1e3a37] px-3 py-2 min-w-[60px] text-center">
      <p className="font-display font-black text-2xl sm:text-3xl !text-white leading-none" data-testid={`countdown-${label.toLowerCase()}`}>{String(value).padStart(2, "0")}</p>
      <p className="text-[9px] uppercase tracking-widest text-[#B87333] mt-1">{label}</p>
    </div>
  );
}

export default function Auction() {
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", phone: "", amount: "" });
  const [submitting, setSubmitting] = useState(false);
  const [buyNowLoading, setBuyNowLoading] = useState(false);
  const [showBuyNowModal, setShowBuyNowModal] = useState(false);

  const refresh = () =>
    api.get("/auctions/active").then((r) => setAuction(r.data)).catch(() => setAuction(null));

  useEffect(() => {
    setLoading(true);
    refresh().finally(() => setLoading(false));
    const poll = setInterval(refresh, 10000);
    // After a Stripe Buy Now redirect, poll checkout-status so the auction
    // flips to SOLD as soon as Stripe confirms payment (works with or without
    // webhook — status endpoint syncs on demand).
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("buy_now_session");
    if (sid) {
      let attempts = 0;
      const t = setInterval(async () => {
        attempts += 1;
        try {
          const s = await api.get(`/checkout/status/${sid}`);
          if (s.data.payment_status === "paid") {
            clearInterval(t);
            toast.success("Payment received — you own the piece! David will be in touch.");
            await refresh();
          }
        } catch (e) { /* ignore */ }
        if (attempts > 20) clearInterval(t);
      }, 3000);
    }
    return () => clearInterval(poll);
  }, []);

  const cd = useCountdown(auction?.end_time);
  const isSold = auction?.status === "sold";
  const isPending = auction?.status === "pending_buy_now";
  const isEnded = !auction || auction.status === "ended" || isSold || cd.ended;
  const hasBids = (auction?.bids || []).length > 0;
  // First bid must simply MEET the starting bid; subsequent bids must beat the current + $1.
  const minBid = hasBids
    ? (Number(auction?.current_highest_bid || 0) + 1).toFixed(2)
    : Number(auction?.starting_bid ?? auction?.current_highest_bid ?? 0).toFixed(2);
  const displayBid = hasBids
    ? Number(auction?.current_highest_bid || 0)
    : Number(auction?.starting_bid ?? auction?.current_highest_bid ?? 0);
  const buyNowPrice = Number(auction?.buy_now_price || 0);
  const buyNowTotal = buyNowPrice ? (buyNowPrice + 25).toFixed(0) : null;
  const canBuyNow = !!buyNowPrice && !isEnded && !isPending;

  const submit = async () => {
    if (submitting) return;
    const amt = parseFloat(form.amount);
    if (!form.name.trim()) return toast.error("Please enter your name.");
    if (!form.email.includes("@")) return toast.error("Enter a valid email.");
    if (form.phone.replace(/\D/g, "").length < 6) return toast.error("A phone number David can reach you on.");
    if (!(amt >= parseFloat(minBid))) {
      return toast.error(hasBids
        ? `Your bid must be at least $${minBid}.`
        : `The starting bid is $${minBid} — bid at least that to open the auction.`);
    }
    setSubmitting(true);
    try {
      await api.post("/auctions/bid", { name: form.name, email: form.email, phone: form.phone, amount: amt });
      toast.success(`Bid of $${amt.toFixed(2)} placed — you're now the highest bidder!`);
      setForm({ ...form, amount: "" });
      await refresh();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSubmitting(false); }
  };

  const startBuyNow = async () => {
    if (buyNowLoading) return;
    if (!form.name.trim()) return toast.error("Please enter your name.");
    if (!form.email.includes("@")) return toast.error("Enter a valid email.");
    if (form.phone.replace(/\D/g, "").length < 6) return toast.error("A phone number David can reach you on.");
    setBuyNowLoading(true);
    try {
      const r = await api.post(`/auctions/${auction.id}/buy-now`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        origin_url: window.location.origin,
      });
      // Off to Stripe — success redirects back to /auction?buy_now_session=...
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(formatErr(e));
      setBuyNowLoading(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] text-[#A3A3A3] p-10">Loading the Auction Room…</div>;

  if (!auction) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5DC] p-10 sm:p-16" data-testid="auction-empty">
        <div className="max-w-3xl mx-auto text-center pt-16">
          <Gavel className="mx-auto text-[#B87333] mb-6" size={48} />
          <h1 className="font-display font-black text-4xl sm:text-6xl mb-4 !text-[#F5F5DC]">The Auction Room</h1>
          <p className="text-[#A3A3A3] leading-relaxed">
            No auction is running right now. Check back soon — I put a one-of-a-kind piece under the hammer every couple of weeks.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5DC] pb-24" data-testid="auction-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-10 sm:pt-16">
        <div className="flex items-center gap-3 mb-3">
          <Gavel className="text-[#B87333]" size={26} />
          <p className="smallcaps !text-[#B87333]">Live Auction</p>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-6xl leading-tight mb-4 !text-[#F5F5DC]" data-testid="auction-product-name">
          {auction.product_name}
        </h1>
        {(auction.auction_description || auction.product_description) && (
          <p
            className="!text-white font-bold text-base sm:text-lg max-w-3xl leading-relaxed mb-8 whitespace-pre-wrap"
            data-testid="auction-description"
          >
            {auction.auction_description || auction.product_description}
          </p>
        )}

        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-8 lg:gap-14">
          <div>
            {auction.product_image ? (
              <img src={auction.product_image} alt={auction.product_name} className="w-full h-auto border border-[#2A2A2A]" />
            ) : (
              <div className="w-full aspect-[4/3] bg-[#111] border border-[#2A2A2A] flex items-center justify-center text-[#555]">No image</div>
            )}
          </div>

          <div className="space-y-8">
            {/* Bid + Countdown */}
            <div className="bg-[#111] border border-[#2A2A2A] p-5 sm:p-6">
              <p className="smallcaps mb-1" data-testid="bid-label">
                {hasBids ? "Current Highest Bid" : "Starting Bid"}
              </p>
              <p className="font-display font-black text-5xl sm:text-6xl !text-white leading-none tracking-tight" data-testid="current-bid">
                ${displayBid.toFixed(0)}
              </p>
              <p className="text-xs text-[#A3A3A3] mt-2">
                {(auction.bids || []).length} bid{(auction.bids || []).length === 1 ? "" : "s"} placed
                {" · "}Winner pays bid + <span className="text-[#F5F5DC] font-bold">$25 shipping</span>
              </p>
              <div className="mt-6">
                <div className="flex items-center gap-2 mb-2">
                  <Clock size={14} className="text-[#B87333]" />
                  <p className="smallcaps">{isEnded ? "Auction Closed" : "Time Remaining"}</p>
                </div>
                {isEnded ? (
                  <div className="border-2 border-[#B87333] bg-[#B87333]/10 p-4 text-center" data-testid="auction-ended">
                    <p className="text-[#B87333] font-bold uppercase tracking-widest text-sm">
                      {isSold
                        ? `Sold via Buy It Now — $${Number(auction.current_highest_bid).toFixed(0)}`
                        : `Auction closed — sold for $${Number(auction.current_highest_bid).toFixed(0)}`}
                    </p>
                  </div>
                ) : isPending ? (
                  <div className="border-2 border-[#F5A623] bg-[#F5A623]/10 p-4 text-center" data-testid="auction-pending">
                    <p className="text-[#F5A623] font-bold uppercase tracking-widest text-sm">
                      Buy It Now purchase in progress — bidding paused
                    </p>
                  </div>
                ) : (
                  <div className="flex gap-2" data-testid="countdown">
                    <Cell label="Days" value={cd.d} />
                    <Cell label="Hrs" value={cd.h} />
                    <Cell label="Min" value={cd.m} />
                    <Cell label="Sec" value={cd.s} />
                  </div>
                )}
              </div>
            </div>

            {/* Buy It Now */}
            {canBuyNow && (
              <div className="bg-gradient-to-br from-[#F5A623]/25 to-[#B87333]/25 border-2 border-[#F5A623] p-5 sm:p-6" data-testid="buy-now-block">
                <p className="smallcaps !text-[#F5A623] mb-1 flex items-center gap-1.5"><Zap size={14} /> Skip the wait</p>
                <p className="font-display font-black text-4xl sm:text-5xl leading-none !text-white" data-testid="buy-now-price">
                  ${buyNowTotal}
                </p>
                <p className="text-xs text-[#F5DEB3] mt-2">
                  Buy It Now for ${Number(buyNowPrice).toFixed(0)} · <span className="text-white font-bold">includes $25 shipping</span>
                </p>
                <button
                  onClick={startBuyNow}
                  disabled={buyNowLoading}
                  data-testid="buy-now-btn"
                  className="w-full h-12 mt-4 inline-flex items-center justify-center gap-2 bg-[#F5A623] text-black font-bold uppercase tracking-wider text-xs hover:bg-[#FFC048] transition-colors disabled:opacity-50"
                >
                  {buyNowLoading
                    ? <><Loader2 size={16} className="animate-spin" /> Redirecting…</>
                    : <><Zap size={14} /> Buy It Now for ${buyNowTotal}</>}
                </button>
                <p className="text-[10px] uppercase tracking-widest text-[#F5DEB3]/70 mt-3">
                  Enter your name, email &amp; phone in the form below before clicking Buy It Now.
                </p>
              </div>
            )}

            {/* Bidding form */}
            {!isEnded && !isPending && (
              <div className="bg-[#111] border border-[#2A2A2A] p-5 sm:p-6" data-testid="bid-form">
                <p className="smallcaps mb-3">Place Your Bid</p>
                <div className="space-y-3">
                  <input type="text" placeholder="Full name *" autoComplete="name"
                    className="input-dark min-h-[48px]" data-testid="bid-name"
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  <input type="email" placeholder="Email address *" autoComplete="email"
                    className="input-dark min-h-[48px]" data-testid="bid-email"
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  <input type="tel" placeholder="Phone number *" autoComplete="tel"
                    className="input-dark min-h-[48px]" data-testid="bid-phone"
                    value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B87333]"><DollarSign size={16} /></span>
                    <input type="number" step="1" min={minBid}
                      placeholder={`Min bid $${minBid}`}
                      className="input-dark min-h-[48px] pl-9" data-testid="bid-amount"
                      value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                  </div>
                </div>
                <button onClick={submit} disabled={submitting} data-testid="bid-submit"
                  className="w-full h-14 mt-4 inline-flex items-center justify-center gap-2 bg-[#B87333] text-white font-bold uppercase tracking-wider text-xs hover:bg-[#A6632B] transition-colors disabled:opacity-50">
                  {submitting ? <><Loader2 size={16} className="animate-spin" /> Placing bid…</> : <><Send size={14} /> Place Bid</>}
                </button>
                <p className="text-[10px] uppercase tracking-widest text-[#757575] mt-3">
                  By bidding you agree the winning bid + $25 shipping is owed on close.
                </p>
              </div>
            )}

            {/* Bid history */}
            {(auction.bids || []).length > 0 && (
              <div className="bg-[#111] border border-[#2A2A2A] p-5 sm:p-6" data-testid="bid-history">
                <p className="smallcaps mb-3">Bid History</p>
                <ul className="space-y-2 text-sm">
                  {[...auction.bids].reverse().map((b, i) => (
                    <li key={i} className="flex justify-between border-b border-[#1a1a1a] pb-1.5">
                      <span className="text-[#A3A3A3]">{b.name}</span>
                      <span className="font-display font-bold text-[#F5F5DC]">${Number(b.amount).toFixed(0)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
